import { TextFieldModule } from '@angular/cdk/text-field';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PromptComponent } from '@components/prompt/prompt.component';
import { Label } from '@models';
import { NotificationService, LabelService } from '@services';
import { ResolveUserPipe } from '@pipes';

@Component({
  selector: 'app-label-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  imports: [
    AsyncPipe,
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    ReactiveFormsModule,
    ResolveUserPipe,
    RouterModule,
    TextFieldModule,
  ],
})
export class LabelFormComponent {
  readonly service = inject(LabelService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly dialog = inject(MatDialog);
  readonly notificationService = inject(NotificationService);

  readonly label = signal<Label | null>(
    this.route.snapshot.data['label'] || null,
  );
  readonly labelName = computed<string>(() => {
    const l = this.label();
    return l ? `(${l.type}) ${l.title}` : '';
  });

  form = new FormGroup({
    type: new FormControl(this.label()?.type || '', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern('[a-z]+')],
    }),
    title: new FormControl(this.label()?.title || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    url: new FormControl(this.label()?.url || '', {
      nonNullable: true,
      validators: [Validators.pattern('^https?://.+')],
    }),
  });

  save() {
    if (this.form.pristine || !this.form.valid) {
      return;
    }
    const label = {
      type: this.form.value.type || '',
      title: this.form.value.title || '',
      url: this.form.value.url || null,
    } as Label;

    const fn = this.label()?.id
      ? this.service.update(this.label()!.id, label)
      : this.service.create(label);
    fn.subscribe({
      next: () => {
        this.router.navigate(['label']).finally(() => {
          this.form.markAsPristine();
        });
      },
      error: (err) => {
        this.notificationService.error('Failed to save changes.');
        console.error(err);
      },
    });
  }

  delete() {
    this.dialog
      .open(PromptComponent, {
        minWidth: '360px',
        width: '480px',
        data: {
          title: 'Delete label',
          content:
            'Confirm to delete this label. This action can not be undone.',
        },
      })
      .afterClosed()
      .subscribe((result: boolean) => {
        if (!result) {
          return;
        }
        this.service.delete(this.label()!.id).subscribe({
          next: () => {
            this.router.navigate(['label']).finally(() => {
              this.notificationService.success(
                `"${this.labelName()}" deleted successfully.`,
              );
            });
          },
          error: (err) => {
            console.error(err);
            this.notificationService.error(
              `Failed to delete "${this.labelName()}".`,
            );
          },
        });
      });
  }
}
